import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../../Components/ui/dialog';
import { Button } from '../../../../Components/ui/button';
import { Checkbox } from '../../../../Components/ui/checkbox';
import { Badge } from '../../../../Components/ui/badge';
import { Input } from '../../../../Components/ui/input';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../../../Components/ui/accordion';

// function extractShortCode(label: string): string {
//   // Looks for digits at end, after dash, with/without spaces
//   const match = label.match(/-\s*(.+)$/);
//   return match ? match[1] : "";
// }

function ensureShortCode(op: OperatorOption): OperatorOption {
  // already provided → keep it
  if (op.short_code && String(op.short_code).length > 0) return op;

  // fallback: infer from id formatted as "<channel_config_id>_<shortcode>"
  if (typeof op.id === 'string' && op.id.includes('_')) {
    const sc = op.id.split('_').pop() ?? '';
    return sc ? { ...op, short_code: sc } : op;
  }
  return op; // if we truly can’t infer it, leave as-is
}

export interface OperatorOption {
  id: string | number;
  label: string;
  channel_config_id: string | number;
  channel_config?: string | object;
  auto_approval?: boolean;
  partner_id?: number;
  channel_config_name?: string;
  short_code?: string;
  status?: string; // <-- add this!
  operator_mapping_id?: number;
  travellerCount?: number;
  phone_id?: string;
  access_token?: string;
  api_type?: string;
  channel_id?: number;
  meta_phone_status?: string;
  warning_reason?: string;
  disabled?: boolean;
  welcomeMessage?: boolean;
  wm_template_id?: number | null;
  wm_template_name?: string | null;
  wm_variables?: Record<string, string> | null; 
  wm_part_count?: number | null;   // ← ADD THIS
  ttl_hours?: number | null;
}

interface OperatorPopupProps {
  open: boolean;
  country: string;
  options: OperatorOption[];
  initial: OperatorOption[];
  onClose: (selected: OperatorOption[]) => void;
  limitToOneOperator?: boolean;
  locked?: boolean;
}

const OperatorPopup: React.FC<OperatorPopupProps> = ({
  open,
  country,
  options,
  initial,
  onClose,
  limitToOneOperator = false,
}) => {
  const [selected, setSelected] = useState<OperatorOption[]>(initial);
  const [search, setSearch] = useState('');

  useEffect(() => setSelected(initial), [initial, open]);
  useEffect(() => setSearch(''), [open]);
  useEffect(() => {
    console.log('options: ' + JSON.stringify(options));
  }); //debug

  // New toggle logic for per-partner_id single selection
  const toggle = (opt: OperatorOption) => {
    if (opt.disabled) return; // ⬅️ block clicks on disabled options
    if (limitToOneOperator) {
      if (selected.some((o) => o.id === opt.id)) {
        setSelected([]);
      } else {
        setSelected([opt]);
      }
    } else if (typeof opt.partner_id !== 'undefined') {
      // Remove any existing selection with same partner_id, then add/remove current
      setSelected((prev) => {
        const withoutSamePartner = prev.filter(
          (o) => o.partner_id !== opt.partner_id
        );
        const isAlreadySelected = prev.some((o) => o.id === opt.id);
        if (isAlreadySelected) {
          return withoutSamePartner;
        }
        return [...withoutSamePartner, opt];
      });
    } else {
      // Fallback: normal multi-select for options without partner_id
      setSelected((prev) =>
        prev.some((o) => o.id === opt.id)
          ? prev.filter((o) => o.id !== opt.id)
          : [...prev, opt]
      );
    }
  };

  // Select all logic (only works for multi-partner_id mode)
  const allSelected = selected.length === options.length && options.length > 0;
  const selectAll = (checked: boolean) => {
    if (!checked) {
      setSelected([]);
      return;
    }
    // For per-partner_id, only one per partner!
    const unique: Record<string, OperatorOption> = {};
    options.forEach((op) => {
      if (typeof op.partner_id !== 'undefined' && !unique[op.partner_id]) {
        unique[op.partner_id] = op;
      }
      // If partner_id is not present, just add it by id
      if (typeof op.partner_id === 'undefined') {
        unique['no_partner_' + op.id] = op;
      }
    });
    setSelected(Object.values(unique));
  };

  // Filtered options based on search
  const filterText = search.trim().toLowerCase();
  const filteredOptions = options.filter((op) => {
    const fields = [
      op.label?.toLowerCase() ?? '',
      ...(op.label?.split(/[_\s-]+/).map((w) => w.toLowerCase()) ?? []),
      op.partner_id?.toString() ?? '',
      op.channel_config_name?.toLowerCase() ?? '',
      op.short_code?.toString() ?? '',
    ];
    // Remove duplicate entries in fields
    const uniqueFields = Array.from(new Set(fields));
    // Match if ANY field contains the filterText
    return uniqueFields.some((field) => field.includes(filterText));
  });

  const groupedOptions = filteredOptions.reduce((acc, option) => {
    const prefix = option.label.split('_')[0]; // Extract MTN / TAN
    if (!acc[prefix]) acc[prefix] = [];
    acc[prefix].push(option);
    return acc;
  }, {} as Record<string, typeof filteredOptions>);

  const shouldScroll = filteredOptions.length > 3;

  return (
    <Dialog open={open} onOpenChange={() => onClose(initial)}>
      <DialogContent className='sm:max-w-[500px] border border-[#E2E8F0]'>
        <DialogHeader>
          <DialogTitle>Manage Operators&nbsp;–&nbsp;{country}</DialogTitle>
        </DialogHeader>

        {limitToOneOperator && (
          <div className='mb-2 text-xs text-red-500'>
            Only one operator can be allocated for this country.
          </div>
        )}

        {/* Search bar */}
        <div className='px-2 mb-2'>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder='Search operator...'
            className='h-8 text-sm'
            autoFocus
          />
        </div>

        {/* Badges for selected operators */}
        <div className='flex flex-wrap gap-2 px-2 mb-2 min-h-[30px]'>
          {selected.length > 0 ? (
            selected.map((op) => (
              <Badge
                key={op.id}
                variant='secondary'
                className='gap-1 pr-1 cursor-pointer'
                onClick={() => toggle(op)}
              >
                {op.label}
                <span className='ml-1'>&times;</span>
              </Badge>
            ))
          ) : (
            <span className='text-xs text-gray-400'>No operators selected</span>
          )}
        </div>

        <div
          className={
            shouldScroll
              ? 'max-h-60 overflow-y-auto divide-y rounded border -mt-4'
              : 'divide-y rounded border -mt-4'
          }
        >
          <Accordion type='single' collapsible className='p-0'>
            {Object.entries(groupedOptions).length > 0 ? (
              Object.entries(groupedOptions).map(
                ([groupName, groupOptions]) => (
                  <AccordionItem
                    key={groupName}
                    value={groupName}
                    className='p-2'
                  >
                    <AccordionTrigger>{groupName}</AccordionTrigger>
                    <AccordionContent>
                      {groupOptions.map((op) => (
                        <div
                          key={op.id}
                          className='px-4 py-2 text-sm text-gray-800 rounded cursor-pointer hover:bg-gray-100'
                        >
                          <label
                            key={op.id}
                            className='flex items-center w-full gap-3 px-4 py-3 text-sm'
                          >
                            <Checkbox
                              checked={selected.some((o) => o.id === op.id)}
                              onCheckedChange={() => toggle(op)}
                              disabled={!!op.disabled}
                            />

                            <span
                              className={`truncate ${
                                op.disabled ? 'text-gray-400' : ''
                              }`}
                            >
                              {op.label}
                            </span>

                            {/* ⚠️ Warning icon when Meta phone status is pending */}
                            {op.meta_phone_status &&
                              op.meta_phone_status.toLowerCase() ===
                                'pending' && (
                                <span
                                  className='ml-2 inline-flex h-5 w-5 items-center justify-center rounded-full border border-gray-300 text-gray-600 text-[11px] font-semibold'
                                  title={
                                    op.warning_reason ||
                                    'WABA status is Pending.'
                                  }
                                >
                                  i
                                </span>
                              )}

                            {typeof op.auto_approval === 'boolean' && (
                              <span
                                className={
                                  op.auto_approval
                                    ? 'ml-2 px-2 py-0.5 rounded text-xs bg-green-100 text-green-600'
                                    : 'ml-2 px-2 py-0.5 rounded text-xs bg-yellow-100 text-yellow-700'
                                }
                              >
                                {op.auto_approval
                                  ? 'Auto Approval'
                                  : 'Manual Approval'}
                              </span>
                            )}
                          </label>
                        </div>
                      ))}
                    </AccordionContent>
                  </AccordionItem>
                )
              )
            ) : (
              <div className='px-4 py-6 text-sm text-center text-gray-400'>
                No operators found
              </div>
            )}
          </Accordion>
        </div>

        {/* Centered submit button */}
        <div className='flex justify-center -mt-2'>
          <Button
            onClick={() => {
              // For each selected operator, if short_code is missing, set it from label
              const selectedWithShortCode = selected.map(ensureShortCode);
              onClose(selectedWithShortCode);
            }}
            disabled={selected.length === 0}
          >
            Submit
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OperatorPopup;
